import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import {
  mobileTextValidator,
  requiredArrayValidator,
  requiredValidator,
} from "../../components/formFields/CommonValidator";
import ShadowCard from "../../components/common/ShadowCard";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import FormImageUpload from "../../components/formFields/FormImageUpload";
import {
  PropertyDetailsImageUpload,
  createProperty,
  deleteGalleryImage,
  deletePropertyImage,
  getPropertyByID,
  updateProperty,
} from "./services/property.services";

import {
  clearGalleryImageID,
  clearPropertyDetails,
  clearPropertyImageID,
  setGalleryImageID,
  setPropertyImageID,
} from "./propertySlice";
// import FormRichTextField from "../../components/formFields/FormRichTextField";
import FormTextArea from "../../components/formFields/FormTextArea";
import { getFile, slugify } from "../../_helper/helper";
import FormRichTextField from "../../components/formFields/FormRichTextField";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import FormCheckbox from "../../components/formFields/FormCheckbox";
import IconComponent from "../../components/common/IconComponent";
import { MdDelete } from "react-icons/md";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import AppDialog, { DialogCloseButton } from "../../components/dialog/Dialog";

const CreateProperty: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const PropertyID = location.state?.PropertyID;
  const PropertyDetail = useAppSelector(
    (state) => state.property.PropertyDetail,
  );
  const loading = useAppSelector((state) => state.property.loading);
  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [PropertyDetail]);

  useEffect(() => {
    if (PropertyID) {
      dispatch(getPropertyByID(PropertyID));
    }
    return () => {
      dispatch(clearPropertyDetails());
    };
  }, [PropertyID]);

  const handleDeleteImage = (ID: number) => {
    dispatch(openDialog("deletePropertyImage"));
    dispatch(setPropertyImageID(ID));
  };

  const handleDeleteGalleryImage = (ID: number) => {
    dispatch(openDialog("deletePropertyImage"));
    dispatch(setGalleryImageID(ID));
  };

  const handleSubmit = async (values: any) => {
    const formData = {
      PropertyName: values?.PropertyName ? values?.PropertyName : "",
      PropertyURL: values?.PropertyURL ? values?.PropertyURL : "",
      BookingURL: values?.BookingURL ? values?.BookingURL : "",
      TagLine: values?.TagLine ? values?.TagLine : "",
      Address: values?.Address ? values?.Address : "",
      MapURL: values?.MapURL ? values?.MapURL : "",
      MobileNo: values?.MobileNo ? `${values?.MobileNo}` : "",
      EmailID: values?.EmailID ? values?.EmailID : "",
      ShortDescription: values?.ShortDescription
        ? values?.ShortDescription
        : "",
      LongDescription: values?.LongDescription ? values?.LongDescription : "",
      DiningInfo: values?.DiningInfo ? values?.DiningInfo : "",
      Tags: values?.Tags ? values?.Tags : "",
      SequenceNo: values?.SequenceNo ? +values?.SequenceNo : null,
      TotalRoom: values?.TotalRoom ? +values?.TotalRoom : null,
      IsHightlightProperty: values?.IsHightlightProperty
        ? values?.IsHightlightProperty
        : false,
      MetaTitle: values?.MetaTitle ? values?.MetaTitle : "",
      MetaDescription: values?.MetaDescription ? values?.MetaDescription : "",
      MetaKeywords: values?.MetaKeywords ? values?.MetaKeywords : "",
      IsActive: values.IsActive === 1,
    };
    const ImageData = new FormData();
    const BannerImages =
      values?.BannerImages && typeof values?.BannerImages !== "string"
        ? getFile(values?.BannerImages)
        : "";
    try {
      if (PropertyID) {
        const editPayload = {
          ...formData,
          ID: PropertyID,
        };

        const response = await dispatch(updateProperty(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            (values?.BannerImages &&
              typeof values?.BannerImages !== "string") ||
            values.attachments?.length > 0 ||
            values.GalleryAttachments?.length > 0
          ) {
            ImageData.append("BannerImages", BannerImages);
            ImageData.append("ID", PropertyID);
            if (values?.attachments && Array.isArray(values.attachments)) {
              values.attachments.forEach((image: any) => {
                if (image.attachment && typeof image.attachment !== "string")
                  return;
                const attachment = getFile(image);
                if (attachment) {
                  ImageData.append("InnerImages", attachment);
                }
              });
            }
            if (
              values?.GalleryAttachments &&
              Array.isArray(values.GalleryAttachments)
            ) {
              values.GalleryAttachments.forEach((image: any) => {
                if (
                  image.GalleryImage &&
                  typeof image.GalleryImage !== "string"
                )
                  return;
                const galleryImage = getFile(image);
                if (galleryImage) {
                  ImageData.append("Galleryimages", galleryImage);
                }
              });
            }
            await dispatch(PropertyDetailsImageUpload(ImageData));
          }
          navigate("/property");
          dispatch(clearPropertyDetails());
        }
      } else {
        const response = await dispatch(createProperty(formData));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            ((values?.BannerImages &&
              typeof values?.BannerImages !== "string") ||
              values.attachments?.length > 0) &&
            response?.payload?.Data?.ID
          ) {
            ImageData.append("BannerImages", BannerImages);
            ImageData.append("ID", response?.payload?.Data?.ID);
            if (values?.attachments && Array.isArray(values.attachments)) {
              values.attachments.forEach((image: any) => {
                if (image.attachment && typeof image.attachment !== "string")
                  return;
                const attachment = getFile(image);
                if (attachment) {
                  ImageData.append("InnerImages", attachment);
                }
              });
            }
            await dispatch(PropertyDetailsImageUpload(ImageData));
          }
          navigate("/property");
          dispatch(clearPropertyDetails());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  // if (loading) return <LoadingPanel gridRef={gridRef} />;
  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <DeletePropertyImageDialog />
      <div>
        <Form
          onSubmit={handleSubmit}
          key={formKey}
          initialValues={PropertyDetail}
          render={(formRenderProps: FormRenderProps) => (
            <FormElement>
              <ShadowCard style={{ padding: 12 }}>
                <GridLayout
                  style={{ marginRight: 20 }}
                  gap={{ rows: 5, cols: 10 }}
                  cols={[
                    { width: "33.33%" },
                    { width: "33.33%" },
                    { width: "33.33%" },
                  ]}
                >
                  <GridLayoutItem colSpan={3}>
                    <Typography.h4>
                      {PropertyID ? "Update Property" : "Add Property"}
                    </Typography.h4>
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={2}>
                    <Field
                      name="PropertyName"
                      label="Property Name"
                      placeholder="Property Name"
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                      onChange={(e: any) => {
                        !PropertyID &&
                          formRenderProps.onChange("PropertyURL", {
                            value: e?.value ? slugify(e?.value) : "",
                          });
                      }}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="PropertyURL"
                      label="Property URL"
                      placeholder="Property URL"
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                      onInput={(e: any) => {
                        e.target.value = slugify(e?.target?.value);
                      }}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={2}>
                    <Field
                      name="BookingURL"
                      label="Booking URL"
                      placeholder="Booking URL"
                      component={FormTextField}
                      onInput={(e: any) => {
                        e.target.value = e?.target?.value
                          ?.trim(" ")
                          ?.replace(/\s+/g, "-");
                      }}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="TagLine"
                      label="Tagline"
                      placeholder="Tagline"
                      component={FormTextField}

                      // validator={requiredValidator}
                      // astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="MobileNo"
                      type="text"
                      label="Mobile No"
                      placeholder="i.e. 9999999999"
                      validator={mobileTextValidator}
                      astrike={true}
                      component={FormTextField}
                      onInput={(e: any) => {
                        let value = e.target.value;
                        // Keep only digits, +, -, comma, and space
                        value = value.replace(/[^0-9+,\-\s]/g, "");
                        // Prevent consecutive ++ or --
                        value = value.replace(/\++/g, "+");
                        value = value.replace(/-+/g, "-");

                        // Allow + only at the start of each number
                        const parts = value.split(",");
                        const cleanedParts = parts.map((part: any) => {
                          const leadingSpaces = part.match(/^\s*/)?.[0] || "";
                          const actualPart = part.slice(leadingSpaces.length);
                          if (actualPart.startsWith("+")) {
                            return (
                              leadingSpaces +
                              "+" +
                              actualPart.slice(1).replace(/\+/g, "")
                            );
                          } else {
                            return (
                              leadingSpaces + actualPart.replace(/\+/g, "")
                            );
                          }
                        });
                        e.target.value = cleanedParts.join(",");
                      }}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="EmailID"
                      type="text"
                      label="Email"
                      placeholder="i.e. example@gmail.com"
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      wrapperClassName="right-aligned-field"
                      name="TotalRoom"
                      label="Total Rooms"
                      placeholder="i.e. 0"
                      format="n0"
                      min={0}
                      max={1000}
                      component={FormNumericTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={3}>
                    <Field
                      name="Address"
                      label="Address"
                      placeholder="Address"
                      component={FormTextArea}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={3}>
                    <Field
                      name="MapURL"
                      label="Map URL"
                      placeholder="Map URL"
                      component={FormTextField}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={3}>
                    <Field
                      name="ShortDescription"
                      label="Short Description"
                      placeholder="Short Description"
                      component={FormRichTextField}
                      style={{ height: 200 }}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={3}>
                    <Field
                      name="LongDescription"
                      label="Long Description"
                      placeholder="Long Description"
                      component={FormRichTextField}
                      style={{ height: 200 }}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={3}>
                    <Field
                      name="DiningInfo"
                      label="Dining Info."
                      placeholder="Dining Info."
                      component={FormRichTextField}
                      style={{ height: 200 }}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="Tags"
                      label="Tags"
                      placeholder="Tags"
                      component={FormTextField}
                      // validator={requiredValidator}
                      // astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      wrapperClassName="right-aligned-field"
                      name="SequenceNo"
                      label="Sequence No."
                      placeholder="0"
                      format="n0"
                      min={0}
                      component={FormNumericTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    style={{
                      marginTop: "18px",
                      background: "#edeaea",
                      borderRadius: 10,
                      padding: "0 10px 15px",
                      fontWeight: 500,
                      color: "black",
                    }}
                  >
                    <Field
                      labelClassName="ms-2"
                      name="IsHightlightProperty"
                      label="Is Hightlight Property"
                      placeholder="Is Hightlight Property"
                      maxLength="100"
                      component={FormCheckbox}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={3}>
                    <Field
                      name="MetaTitle"
                      label="Meta Title"
                      placeholder="Meta Title"
                      component={FormTextField}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={3}>
                    <Field
                      name="MetaKeywords"
                      label="Meta Keywords"
                      placeholder="Meta Keywords"
                      component={FormTextField}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={3}>
                    <Field
                      name="MetaDescription"
                      label="Meta Description"
                      placeholder="Meta Description"
                      component={FormTextArea}
                    />
                  </GridLayoutItem>

                  <GridLayoutItem>
                    <Field
                      justifyContent="start"
                      name={`BannerImages`}
                      label="Banner Image"
                      type="file"
                      accept="image/*"
                      alternateName="Banner Image"
                      component={FormImageUpload}
                      astrike={true}
                      validator={requiredArrayValidator}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      justifyContent="start"
                      name={`attachments`}
                      label="Inner Images"
                      type="file"
                      multiple
                      accept="image/*"
                      alternateName="Inner Images"
                      component={FormImageUpload}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      justifyContent="start"
                      name={`GalleryAttachments`}
                      label="Gallery Images"
                      type="file"
                      multiple
                      accept="image/*"
                      alternateName="Gallery Images"
                      component={FormImageUpload}
                    />
                  </GridLayoutItem>
                  {PropertyDetail?.InnerImages &&
                    PropertyDetail?.InnerImages?.length > 0 && (
                      <GridLayoutItem colSpan={3} className="mt-3">
                        <div className="card shadow-sm border-0">
                          {PropertyDetail?.InnerImages?.length > 0 && (
                            <div
                              className="card-body p-2 pt-3 pe-3"
                              style={{
                                background: "#f8f9fa",
                                borderRadius: "8px",
                                border: "1px solid #e0e0e0",
                              }}
                            >
                              <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5
                                  className="mb-0"
                                  style={{
                                    color: "#2c3e50",
                                    fontWeight: "600",
                                    fontSize: "1.1rem",
                                  }}
                                >
                                  Inner Images
                                </h5>
                                <span className="badge bg-kendo-primary rounded-pill">
                                  {PropertyDetail?.InnerImages?.length || 0}{" "}
                                  files
                                </span>
                              </div>

                              <div className="d-flex flex-wrap gap-3">
                                {PropertyDetail?.InnerImages?.map(
                                  (image: any) => (
                                    <div
                                      key={image.ID}
                                      className="position-relative"
                                      style={{
                                        width: "150px",
                                        transition: "all 0.3s ease",
                                      }}
                                    >
                                      <div className="card h-100 border-0 shadow-sm overflow-hidden">
                                        <div className="ratio ratio-1x1 bg-light overflow-hidden">
                                          <div
                                            style={{
                                              position: "absolute",
                                              width: "100%",
                                              height: "auto",
                                              display: "flex",
                                              justifyContent: "end",
                                            }}
                                          >
                                            <Button
                                              type="button"
                                              themeColor={"error"}
                                              fillMode={"solid"}
                                              style={{
                                                position: "relative",
                                                padding: "5px 10px",
                                                width: 30,
                                                height: 30,

                                                color: "#fff",
                                                zIndex: "1",
                                                borderRadius: "0 0 0 5px",
                                              }}
                                              onClick={() =>
                                                handleDeleteImage(image.ID)
                                              }
                                            >
                                              <IconComponent
                                                icon={MdDelete}
                                                className="absolute-position fs-5"
                                              />
                                            </Button>
                                          </div>
                                          <img
                                            className="img-fluid object-fit-cover"
                                            src={image.PropertyGalleryImages}
                                            alt="Uploaded document"
                                            style={{
                                              cursor: "pointer",
                                              transition: "transform 0.3s",
                                            }}
                                            onMouseEnter={(e) =>
                                              (e.currentTarget.style.transform =
                                                "scale(1.05)")
                                            }
                                            onMouseLeave={(e) =>
                                              (e.currentTarget.style.transform =
                                                "scale(1)")
                                            }
                                            onClick={() =>
                                              window.open(
                                                `${image.PropertyGalleryImages}`,
                                                "_blank",
                                              )
                                            }
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  ),
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </GridLayoutItem>
                    )}
                  {PropertyDetail?.GalleryImages &&
                    PropertyDetail?.GalleryImages?.length > 0 && (
                      <GridLayoutItem colSpan={3} className="mt-3">
                        <div className="card shadow-sm border-0">
                          {PropertyDetail?.GalleryImages?.length > 0 && (
                            <div
                              className="card-body p-2 pt-3 pe-3"
                              style={{
                                background: "#f8f9fa",
                                borderRadius: "8px",
                                border: "1px solid #e0e0e0",
                              }}
                            >
                              <div className="d-flex justify-content-between align-items-center mb-3">
                                <h5
                                  className="mb-0"
                                  style={{
                                    color: "#2c3e50",
                                    fontWeight: "600",
                                    fontSize: "1.1rem",
                                  }}
                                >
                                  Gallery Images
                                </h5>
                                <span className="badge bg-kendo-primary rounded-pill">
                                  {PropertyDetail?.GalleryImages?.length || 0}{" "}
                                  files
                                </span>
                              </div>

                              <div className="d-flex flex-wrap gap-3">
                                {PropertyDetail?.GalleryImages?.map(
                                  (image: any) => (
                                    <div
                                      key={image.ID}
                                      className="position-relative"
                                      style={{
                                        width: "150px",
                                        transition: "all 0.3s ease",
                                      }}
                                    >
                                      <div className="card h-100 border-0 shadow-sm overflow-hidden">
                                        <div className="ratio ratio-1x1 bg-light overflow-hidden">
                                          <div
                                            style={{
                                              position: "absolute",
                                              width: "100%",
                                              height: "auto",
                                              display: "flex",
                                              justifyContent: "end",
                                            }}
                                          >
                                            <Button
                                              type="button"
                                              themeColor={"error"}
                                              fillMode={"solid"}
                                              style={{
                                                position: "relative",
                                                padding: "5px 10px",
                                                width: 30,
                                                height: 30,

                                                color: "#fff",
                                                zIndex: "1",
                                                borderRadius: "0 0 0 5px",
                                              }}
                                              onClick={() =>
                                                handleDeleteGalleryImage(
                                                  image.ID,
                                                )
                                              }
                                            >
                                              <IconComponent
                                                icon={MdDelete}
                                                className="absolute-position fs-5"
                                              />
                                            </Button>
                                          </div>
                                          <img
                                            className="img-fluid object-fit-cover"
                                            src={image.MainGalleryImages}
                                            alt="Uploaded document"
                                            style={{
                                              cursor: "pointer",
                                              transition: "transform 0.3s",
                                            }}
                                            onMouseEnter={(e) =>
                                              (e.currentTarget.style.transform =
                                                "scale(1.05)")
                                            }
                                            onMouseLeave={(e) =>
                                              (e.currentTarget.style.transform =
                                                "scale(1)")
                                            }
                                            onClick={() =>
                                              window.open(
                                                `${image.MainGalleryImages}`,
                                                "_blank",
                                              )
                                            }
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  ),
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </GridLayoutItem>
                    )}
                  <GridLayoutItem style={{ width: "100%", paddingTop: "10px" }}>
                    <Field
                      name={`IsActive`}
                      label="Status"
                      placeholder="i.e. Active"
                      component={FormSelectionField}
                      validator={requiredValidator}
                      astrike={true}
                      options={STATUSARRAY?.map((status: any) => {
                        return {
                          value: status?.value,
                          label: status?.label,
                        };
                      })}
                    />
                  </GridLayoutItem>

                  <GridLayoutItem
                    colSpan={3}
                    style={{
                      marginTop: 20,
                      textAlign: "end",
                    }}
                  >
                    <div>
                      <ButtonWithLoading
                        label={PropertyID ? "Update" : "Save"}
                        type="submit"
                        disabled={!formRenderProps.allowSubmit || loading}
                        loading={loading}
                      />
                      <Button
                        type="button"
                        fillMode={"outline"}
                        themeColor={"primary"}
                        style={{ marginLeft: 10 }}
                        onClick={() => navigate("/property")}
                      >
                        Cancel
                      </Button>
                    </div>
                  </GridLayoutItem>
                </GridLayout>
              </ShadowCard>
            </FormElement>
          )}
        />
      </div>
    </>
  );
};

const DeletePropertyImageDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const location = useLocation();
  const PropertyID = location.state?.PropertyID;

  const PropertyImageID = useAppSelector(
    (state) => state.property.PropertyImageID,
  );
  const GalleryImageID = useAppSelector(
    (state) => state.property.GalleryImageID,
  );

  const handleDeleteAction = async (ID: number | null) => {
    if (ID) {
      const response = PropertyImageID
        ? await dispatch(deletePropertyImage(ID))
        : await dispatch(deleteGalleryImage(ID));
      if (response.meta.requestStatus === "fulfilled") {
        if (PropertyID) {
          dispatch(getPropertyByID(PropertyID));
        }
        dispatch(closeDialog());
        dispatch(clearPropertyImageID());
        dispatch(clearGalleryImageID());
      } else {
        if (PropertyID) {
          dispatch(getPropertyByID(PropertyID));
        }
        dispatch(closeDialog());
        dispatch(clearPropertyImageID());
        dispatch(clearGalleryImageID());
      }
    }
  };
  return (
    <>
      {dialogName === "deletePropertyImage" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Image?"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this Image?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() =>
                    handleDeleteAction(PropertyImageID || GalleryImageID)
                  }
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

export default CreateProperty;
